/**
 * useTrades – trade history, stats, pot state, positions, and agent decision log.
 */
import { create } from 'zustand'
import {
  api,
  getExecutedTrades, getTradeStats, getPotState, simulateTrade,
  getPositions, getPositionSummary, getClosedPositions, getPnlHistory,
  addPosition as apiAddPosition,
  updatePosition as apiUpdatePosition,
  closePosition as apiClosePosition,
} from '@/lib/api'
import type {
  ExecutedTrade, TradeStats, PotState,
  Position, PositionSummary, ClosedPosition, PnlHistoryPoint,
} from '@/lib/types'

export interface Trade {
  id: string
  date: string
  asset: string
  symbol: string
  side: 'Buy' | 'Sell'
  price: number
  quantity: number
  value: number
  status: 'Executed' | 'Pending' | 'Cancelled'
  color: string
  agent: string
}

export interface AgentDecision {
  id: string
  agent_id: string
  agent_acronym: string
  agent_type: string
  decision: string
  confidence: number
  timestamp: string
}

interface TradeState {
  trades: Trade[]
  executedTrades: ExecutedTrade[]
  stats: TradeStats | null
  potState: PotState | null
  decisions: AgentDecision[]
  positions: Position[]
  positionSummary: PositionSummary | null
  closedPositions: ClosedPosition[]
  pnlHistory: PnlHistoryPoint[]
  loadingTrades: boolean
  loadingStats: boolean
  loadingPot: boolean
  loadingDecisions: boolean
  loadingPositions: boolean
  simulating: boolean
  fetchTrades: (limit?: number) => Promise<void>
  fetchExecutedTrades: () => Promise<void>
  fetchStats: () => Promise<void>
  fetchPotState: () => Promise<void>
  fetchDecisions: (limit?: number) => Promise<void>
  fetchPositions: () => Promise<void>
  fetchPositionSummary: () => Promise<void>
  fetchClosedPositions: () => Promise<void>
  fetchPnlHistory: () => Promise<void>
  simulate: (ticker: string, action: string, size_gbp: number) => Promise<void>
  addPosition: (body: {
    ticker: string; name?: string; action: string; entry_price: number;
    quantity: number; stop_loss?: number; take_profit?: number; notes?: string;
  }) => Promise<void>
  updatePosition: (id: number, body: {
    stop_loss?: number; take_profit?: number; notes?: string;
  }) => Promise<void>
  closePosition: (id: number, body?: {
    exit_price?: number; notes?: string;
  }) => Promise<void>
}

export const useTrades = create<TradeState>()((set, get) => ({
  trades: [],
  executedTrades: [],
  stats: null,
  potState: null,
  decisions: [],
  positions: [],
  positionSummary: null,
  closedPositions: [],
  pnlHistory: [],
  loadingTrades: false,
  loadingStats: false,
  loadingPot: false,
  loadingDecisions: false,
  loadingPositions: false,
  simulating: false,

  fetchTrades: async (limit = 20) => {
    set({ loadingTrades: true })
    try {
      const { data } = await api.get('/api/portfolio/trades', { params: { limit } })
      set({ trades: data.trades, loadingTrades: false })
    } catch { set({ loadingTrades: false }) }
  },

  fetchExecutedTrades: async () => {
    set({ loadingTrades: true })
    try {
      const data = await getExecutedTrades()
      set({ executedTrades: data, loadingTrades: false })
    } catch { set({ loadingTrades: false }) }
  },

  fetchStats: async () => {
    set({ loadingStats: true })
    try {
      const data = await getTradeStats()
      set({ stats: data, loadingStats: false })
    } catch { set({ loadingStats: false }) }
  },

  fetchPotState: async () => {
    set({ loadingPot: true })
    try {
      const data = await getPotState()
      set({ potState: data, loadingPot: false })
    } catch { set({ loadingPot: false }) }
  },

  fetchDecisions: async (limit = 20) => {
    set({ loadingDecisions: true })
    try {
      const { data } = await api.get('/api/agents/decisions', { params: { limit } })
      set({ decisions: data.decisions, loadingDecisions: false })
    } catch { set({ loadingDecisions: false }) }
  },

  fetchPositions: async () => {
    set({ loadingPositions: true })
    try {
      const data = await getPositions()
      set({ positions: data, loadingPositions: false })
    } catch { set({ loadingPositions: false }) }
  },

  fetchPositionSummary: async () => {
    try {
      const data = await getPositionSummary()
      set({ positionSummary: data })
    } catch { /* ignore */ }
  },

  fetchClosedPositions: async () => {
    try {
      const data = await getClosedPositions()
      set({ closedPositions: data })
    } catch { /* ignore */ }
  },

  fetchPnlHistory: async () => {
    try {
      const data = await getPnlHistory()
      set({ pnlHistory: data })
    } catch { /* ignore */ }
  },

  simulate: async (ticker, action, size_gbp) => {
    set({ simulating: true })
    try {
      await simulateTrade({ ticker, action, size_gbp })
      set({ simulating: false })
      // Refresh data
      const trades = await getExecutedTrades()
      const stats = await getTradeStats()
      const pot = await getPotState()
      set({ executedTrades: trades, stats, potState: pot })
    } catch { set({ simulating: false }) }
  },

  addPosition: async (body) => {
    try {
      await apiAddPosition(body)
      const positions = await getPositions()
      const summary = await getPositionSummary()
      set({ positions, positionSummary: summary })
    } catch { /* ignore */ }
  },

  updatePosition: async (id, body) => {
    try {
      await apiUpdatePosition(id, body)
      const positions = await getPositions()
      set({ positions })
    } catch { /* ignore */ }
  },

  closePosition: async (id, body) => {
    try {
      await apiClosePosition(id, body)
      const positions = await getPositions()
      const summary = await getPositionSummary()
      const closed = await getClosedPositions()
      const history = await getPnlHistory()
      set({ positions, positionSummary: summary, closedPositions: closed, pnlHistory: history })
    } catch { /* ignore */ }
  },
}))
